'use client';

import { useMemo } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import styles from './Step3HealthVariant.module.css';
import {
  HEALTH_BENEFIT_KEYS,
  HEALTH_VARIANT_IDS,
  HEALTH_VARIANT_PLANS,
  formatHealthCompensation,
  formatHealthEur,
  formatHealthLimit,
} from './healthVariantPlans';

function buildHealthVariantPackages(t) {
  return HEALTH_VARIANT_IDS.map(id => {
    const plan = HEALTH_VARIANT_PLANS[id];
    const benefits = HEALTH_BENEFIT_KEYS.map(key => ({
      key,
      label: t(`calculator.step3_health_variant.benefits.${key}`),
      limit: formatHealthLimit(plan.benefits[key]),
      compensation: formatHealthCompensation(plan.benefits[key]),
      included: plan.benefits[key] != null,
    }));
    const { pricing } = plan;
    return {
      id,
      title: t(`calculator.forms.health_variant_${id}`),
      benefits,
      pricing: {
        premium: `*${formatHealthEur(pricing.premiumEur)}`,
        securityFee: formatHealthEur(pricing.securityFeeEur),
        annual: formatHealthEur(pricing.annualEur),
        monthly: `~${pricing.monthlyApproxEur} Eur`,
      },
    };
  });
}

/** Shared health variant picker — skaiciuokle step 3 + partner CMS. */
export default function HealthVariantPicker({ variant, onVariantChange }) {
  const { t } = useLanguage();
  const packages = useMemo(() => buildHealthVariantPackages(t), [t]);
  const selectedVariant = variant != null ? Number(variant) : null;
  const selected =
    packages.find(pkg => pkg.id === selectedVariant) || packages[0] || null;

  const handleSelectVariant = variantId => {
    onVariantChange?.(variantId);
  };

  if (!selected) return null;

  return (
    <div className={styles.container}>
      <fieldset className={styles.fieldset}>
        <legend>
          <h2 className={styles.question}>
            {t('calculator.step3_health_variant.question')}
          </h2>
        </legend>
        <p className={styles.hint}>
          {t('calculator.step3_health_variant.hint')}
        </p>
        <div
          className={styles.optionsRow}
          role='radiogroup'
          aria-label={t('calculator.step3_health_variant.question')}
        >
          {packages.map(pkg => {
            const isChecked = selectedVariant === pkg.id;
            return (
              <label key={pkg.id} className={styles.option}>
                <input
                  type='radio'
                  name='health_variant'
                  value={String(pkg.id)}
                  checked={isChecked}
                  onChange={() => handleSelectVariant(pkg.id)}
                  className={styles.optionInput}
                  aria-checked={isChecked}
                />
                <span className={styles.optionButton}>
                  <span className={styles.optionTitle}>{pkg.title}</span>
                  <span className={styles.optionPremium}>{pkg.pricing.monthly}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className={styles.resultPanel}>
        <div className={styles.resultTop}>
          <div className={styles.resultZone}>
            <p className={styles.resultLabel}>
              {selectedVariant != null
                ? t('calculator.step3_health_variant.selected_variant_label')
                : t('calculator.forms.health_variant_label')}
            </p>
            <p className={styles.resultValue}>{selected.title}</p>
            <p className={styles.resultNote}>
              {t('calculator.step3_health_variant.annual_total')}{' '}
              {selected.pricing.annual}
            </p>
          </div>
          <div className={`${styles.resultZone} ${styles.resultZoneRight}`}>
            <p className={styles.resultLabel}>
              {t('calculator.step3_health_variant.monthly_installment')}
            </p>
            <p className={`${styles.resultValue} ${styles.resultValueBrand}`}>
              {selected.pricing.monthly}
            </p>
            <p className={styles.resultNote}>
              {t('calculator.step3_health_variant.premium_per_person')}{' '}
              {selected.pricing.premium}
            </p>
          </div>
        </div>

        <hr className={styles.divider} />

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope='col' className={styles.thProgram}>
                  {t('calculator.step3_health_variant.col_program')}
                </th>
                <th scope='col'>{t('calculator.step3_health_variant.col_limit')}</th>
                <th scope='col'>
                  {t('calculator.step3_health_variant.col_compensation')}
                </th>
              </tr>
            </thead>
            <tbody>
              {selected.benefits.map(row => (
                <tr
                  key={row.key}
                  className={row.included ? undefined : styles.rowMuted}
                >
                  <td className={styles.tdProgram}>{row.label}</td>
                  <td>{row.limit}</td>
                  <td>{row.compensation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.pricing}>
          <div className={styles.pricingRow}>
            <span>{t('calculator.step3_health_variant.premium_per_person')}</span>
            <span className={styles.pricingValue}>{selected.pricing.premium}</span>
          </div>
          <div className={styles.pricingRow}>
            <span>{t('calculator.step3_health_variant.security_fee')}</span>
            <span className={styles.pricingValue}>{selected.pricing.securityFee}</span>
          </div>
          <div className={`${styles.pricingRow} ${styles.pricingRowHighlight}`}>
            <span>{t('calculator.step3_health_variant.annual_total')}</span>
            <span className={styles.pricingValueStrong}>{selected.pricing.annual}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
